import React from 'react';
import { Button } from '@material-ui/core';
import LocationCityIcon from '@material-ui/icons/LocationCity';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import { GridColDef } from '@mui/x-data-grid';
import { formatDetailValue } from '../../formatDetailValue';
import { useEditTeamDrawer } from '../EditTeamDrawer/EditTeamDrawer';
import { Team } from '../TeamsGrid/types';
import { BackToTeams } from './BackToTeams';
import {
  AccountChip,
  ChipRow,
  EmailCell,
  EmailLink,
  InfoField,
  InfoFieldHeader,
  InfoFieldLabel,
  InfoFieldValue,
  InfoGrid,
  LocationChip,
  LocationGroup,
  LocationSite,
  MembersDataGrid,
  PageToolbar,
  Section,
  SectionTitle,
} from './styles';

interface TeamDetailsProps {
  team: Team;
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <InfoField>
      <InfoFieldHeader>
        {icon}
        <InfoFieldLabel>{label}</InfoFieldLabel>
      </InfoFieldHeader>
      <InfoFieldValue>{formatDetailValue(value)}</InfoFieldValue>
    </InfoField>
  );
}

const MEMBER_COLUMNS: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 180,
    sortable: false,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1.2,
    minWidth: 240,
    sortable: false,
    renderCell: ({ row }) => (
      <EmailCell>
        <EmailIcon color="primary" fontSize="small" />
        <EmailLink href={`mailto:${row.email}`}>{row.email}</EmailLink>
      </EmailCell>
    ),
  },
  {
    field: 'roles',
    headerName: 'Roles & Attributes',
    flex: 1,
    minWidth: 180,
    sortable: false,
  },
];

export function TeamDetails({ team }: TeamDetailsProps) {
  const { openEditTeam } = useEditTeamDrawer();

  return (
    <>
      <PageToolbar>
        <BackToTeams />
        <Button
          variant="contained"
          color="primary"
          onClick={() => openEditTeam(team)}
        >
          Edit Team
        </Button>
      </PageToolbar>

      <Section>
        <SectionTitle color="textPrimary" variant="h6">
          Team Information
        </SectionTitle>
        <InfoGrid>
          <InfoItem
            icon={<PersonIcon color="action" fontSize="medium" />}
            label="Name"
            value={team.name}
          />
          <InfoItem
            icon={<LocationCityIcon color="action" fontSize="medium" />}
            label="Parent Team"
            value={team.parentTeam}
          />
          <InfoItem
            icon={<SettingsApplicationsIcon color="action" fontSize="medium" />}
            label="Application"
            value={
              team.applications.length > 0
                ? team.applications.join(', ')
                : undefined
            }
          />
        </InfoGrid>
      </Section>

      <Section>
        <SectionTitle color="textPrimary" variant="h6">
          Accounts
        </SectionTitle>
        <ChipRow>
          {team.accounts.map(account => (
            <AccountChip key={account} color="primary" label={account} />
          ))}
        </ChipRow>
      </Section>

      <Section>
        <SectionTitle color="textPrimary" variant="h6">
          Locations
        </SectionTitle>
        {team.locations.map((location, index) => (
          <LocationGroup key={`${location.site}-${index}`}>
            <LocationSite>{location.site}</LocationSite>
            <ChipRow>
              {location.dataCenters.map((dataCenter, chipIndex) => (
                <LocationChip
                  key={`${dataCenter}-${chipIndex}`}
                  color="primary"
                  label={dataCenter}
                  variant="outlined"
                />
              ))}
            </ChipRow>
          </LocationGroup>
        ))}
      </Section>

      <Section>
        <SectionTitle color="textPrimary" variant="h6">
          Direct Members
        </SectionTitle>
        <MembersDataGrid
          rows={team.members}
          columns={MEMBER_COLUMNS}
          getRowId={member => member.id}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
        />
      </Section>
    </>
  );
}
